// Namespace
import * as ProposalNS from '../Proposal';
import { DataPoints } from '../DAOs/DataPoints';
import { COMPARISON_ITEM_TYPES } from '../../../utils/constants';
import { PROPOSAL_ITEM_PURPOSE } from '../utils/constants';
import { formatLocalizedString } from '../../../utils/i18n';
import ComparisonItem from '@/types/comparison-item';
import ProposalOrientation from '@/types/proposal-orientation';
import Locale from '@/types/locale';

class ProposalRebalanceAnalysisBuilder {
  newSections: ProposalNS.ProposalTemplateSectionElement[] = [];

  section: ProposalNS.ProposalTemplateSectionElement;

  proposalItems: ComparisonItem[];

  orientation: ProposalOrientation;

  locale: Locale;

  constructor(section: ProposalNS.ProposalTemplateSectionElement, proposalItems: ComparisonItem[], orientation: ProposalOrientation, locale: Locale) {
    this.section = section;
    this.proposalItems = proposalItems;
    this.orientation = orientation;
    this.locale = locale;
  }

  build = () => {
    // Find how many rows we should be able to fit on a page
    let maxRowsPerPage: number | undefined;

    // Filter item DAOs to only include those that have purpose flags
    const eligibleItems = this.proposalItems.filter(
      item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && item.flags?.some(flag => Object.values<string>(PROPOSAL_ITEM_PURPOSE).includes(flag)),
    );

    const currentItem = eligibleItems.find(item => item.flags?.includes(PROPOSAL_ITEM_PURPOSE.CURRENT));
    const recommendedItems = eligibleItems.filter(item => item.flags?.includes(PROPOSAL_ITEM_PURPOSE.RECOMMENDED));

    if (!currentItem || !recommendedItems) {
      this.newSections = [];
      return;
    }

    if (this.section.layout.elements.length < 3) {
      // A bit of a hack - but to function properly, the rebalance analysis is expecting 3 elements.
      // First element is a text header, 2nd element is a data element for the rebalance holdings,
      // and third element is a data element for the divest holdings. If either are missing,
      // assume a wrong config and do not generate any sections.
      this.newSections = [];
      return;
    }

    for (const element of this.section.layout.elements) {
      if (
        element.type === ProposalNS.ProposalTemplateElementType.Data &&
        element.dataView.type === ProposalNS.DataView.Table &&
        ProposalNS.DAOType.RebalanceAnalysis
      ) {
        maxRowsPerPage = element.dataView.maxRowsPerPage;
      }
    }

    // If maxRowsPerPage was not defined, just leave the section as is - will most likely break the layout,
    // but at that point this is a template issue.
    if (!maxRowsPerPage) {
      this.newSections = [this.section];
      return;
    }

    // Loop through each recommended portfolios
    recommendedItems.forEach(recommendedItem => {
      const { response: analysisData } = DataPoints.getRebalanceAnalysisData(currentItem, recommendedItem);
      let rebalancedHoldings = analysisData.rebalance;
      let divestHoldings = analysisData.divest;

      const headingTemplateElement = this.section.layout.elements[0] as ProposalNS.ProposalTemplateLayoutElement;
      const rebalanceTemplateElement = this.section.layout.elements[1] as ProposalNS.ProposalTemplateDataElement;
      const divestTemplateElement = this.section.layout.elements[2] as ProposalNS.ProposalTemplateDataElement;

      const headingElement: ProposalNS.ProposalTemplateLayoutElement = {
        ...headingTemplateElement,
        elements: headingTemplateElement.elements.map(element => {
          element = element as ProposalNS.ProposalTemplateDataElement;
          element.DAO = element.DAO as ProposalNS.HTMLContentConfig;

          return {
            ...element,
            DAO: {
              ...element.DAO,
              htmlContentDAOOptions: {
                ...element.DAO.htmlContentDAOOptions,
                html: element.DAO.htmlContentDAOOptions.html?.map(item => ({
                  ...item,
                  content: Object.fromEntries(
                    Object.entries(item.content).map(([locale, value]) => [
                      locale,
                      value?.replace('{{item.name.locale}}', formatLocalizedString(recommendedItem.name, locale as Locale)),
                    ]),
                  ),
                })),
              },
            },
          };
        }),
      };

      while (rebalancedHoldings.length > 0 || divestHoldings.length > 0) {
        const sectionHoldings = rebalancedHoldings.slice(0, maxRowsPerPage);
        rebalancedHoldings = rebalancedHoldings.slice(maxRowsPerPage);

        /** @ts-ignore */
        const divestCount = maxRowsPerPage - sectionHoldings.length;
        const sectionDivestHoldings = divestHoldings.slice(0, divestCount);
        divestHoldings = divestHoldings.slice(divestCount);

        let rebalanceElement: ProposalNS.ProposalTemplateDataElement | undefined;
        let divestElement: ProposalNS.ProposalTemplateDataElement | undefined;

        if (sectionHoldings.length > 0) {
          rebalanceElement = {
            ...rebalanceTemplateElement,
            DAO: {
              ...rebalanceTemplateElement.DAO,
              name: recommendedItem.name,
              holdings: sectionHoldings,
            } as ProposalNS.RebalanceAnalysisConfig,
          };
        }

        if (sectionDivestHoldings.length > 0) {
          divestElement = {
            ...divestTemplateElement,
            DAO: {
              ...divestTemplateElement.DAO,
              holdings: sectionDivestHoldings,
            } as ProposalNS.RebalanceAnalysisConfig,
          };
        }

        this.newSections.push({
          ...this.section,
          layout: {
            ...this.section.layout,
            elements: [
              // First element is a text header - always show
              headingElement,
              rebalanceElement,
              divestElement,
            ].filter(element => element != null) as ProposalNS.ProposalTemplateElement[],
          },
        });
      }
    });
  };
}

export const buildRebalanceAnalysis = (
  section: ProposalNS.ProposalTemplateSectionElement,
  proposalItems: ComparisonItem[],
  orientation: ProposalOrientation,
  locale: Locale,
): ProposalNS.ProposalTemplateSectionElement[] => {
  const builder = new ProposalRebalanceAnalysisBuilder(section, proposalItems, orientation, locale);
  builder.build();

  return builder.newSections;
};
